import { Button, InlineNotification, NumberInput, SkeletonText, Stack, Toggle } from '@carbon/react';
import { asProblem, hasPermission, PageHeader, problemMessage, useAdmin } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';

const settingsKey = ['ho', 'audit-settings'] as const;

/**
 * How long the audit log is kept (ADR 0118): a number of months, or everything. Configuration, never code: it follows the tenant's
 * retention policy, and every change to it is itself audited. Entries older than the period are removed daily at HO.
 */
export function AuditSettingsPage() {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const canManage = hasPermission(session, 'ho.org.audit_settings.manage');
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: settingsKey,
    queryFn: async () => {
      const { data, error } = await hoApi.GET('/api/v1/identity/audit-settings');
      if (!data) throw error;
      return data;
    },
  });
  const [edits, setEdits] = useState<{ keepAll?: boolean; months?: number }>({});

  const saved = current.data;
  const savedMonths = saved?.retentionMonths == null ? null : Number(saved.retentionMonths);
  const keepAll = edits.keepAll ?? savedMonths === null;
  const months = edits.months ?? savedMonths ?? 120;
  const invalid = !keepAll && (!Number.isInteger(months) || months < 1 || months > 600);

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await hoApi.PUT('/api/v1/identity/audit-settings', {
        body: { retentionMonths: keepAll ? null : months, version: saved?.version ?? 0 },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (next) => {
      setEdits({});
      queryClient.setQueryData(settingsKey, next);
      void queryClient.invalidateQueries({ queryKey: ['ho', 'tenant-branding'] }); // the same tenant row's version
    },
  });

  if (current.isLoading) return <SkeletonText paragraph lineCount={3} aria-label={t('common.loading')} />;
  if (!saved)
    return <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(current.error))} />;

  const changed = Object.keys(edits).length > 0;
  return (
    <section className="pos-admin-page">
      <PageHeader title={t('admin.auditSettings.title')} description={t('admin.auditSettings.hint')} />
      <Stack gap={6}>
        <Toggle
          id="audit-keep-all"
          labelText={t('admin.auditSettings.keepAll')}
          labelA={t('admin.no')}
          labelB={t('admin.yes')}
          toggled={keepAll}
          disabled={!canManage}
          onToggle={(on) => {
            save.reset();
            setEdits((all) => ({ ...all, keepAll: on }));
          }}
        />
        {!keepAll && (
          <NumberInput
            id="audit-months"
            label={t('admin.auditSettings.months')}
            helperText={saved.isDefault ? t('admin.auditSettings.default') : t('admin.auditSettings.monthsHelp')}
            min={1}
            max={600}
            step={1}
            value={months}
            readOnly={!canManage}
            invalid={invalid}
            invalidText={t('admin.auditSettings.monthsInvalid')}
            onChange={(_event, { value }) => {
              save.reset();
              setEdits((all) => ({ ...all, months: Number(value) }));
            }}
          />
        )}
        {canManage && (
          <div>
            <Button disabled={!changed || invalid || save.isPending} onClick={() => save.mutate()}>
              {t('admin.save')}
            </Button>
          </div>
        )}
        {save.error && (
          <InlineNotification kind="error" role="alert" lowContrast hideCloseButton title={problemMessage(t, asProblem(save.error))} />
        )}
        {save.isSuccess && (
          <InlineNotification kind="success" role="status" lowContrast hideCloseButton title={t('admin.auditSettings.saved')} />
        )}
      </Stack>
    </section>
  );
}
